import { authHeader } from "../helpers";
import {
  apiFetch,
  bodyLooksLikeHtml,
  NonJsonResponseError,
  parseJsonFromText,
} from "../utils/http/readJsonResponse";
import { extractApiResponseErrorMessage } from "../utils/http/apiError";

export {
  getApiErrorMessage,
  extractApiResponseErrorMessage,
  getHttpStatus,
  isUnauthorizedHttpError,
} from "../utils/http/apiError";

export interface AuthenticatedFetchOptions extends Omit<RequestInit, "body"> {
  body?: object | string;
  throwOnHttpError?: boolean;
}

export async function authenticatedFetch<T = unknown>(
  url: string,
  options: AuthenticatedFetchOptions = {}
): Promise<T> {
  const { body, headers: customHeaders, throwOnHttpError = true, ...initRest } = options;
  const fetchInit: RequestInit = { ...initRest };
  const headers = new Headers(authHeader() as HeadersInit);
  if (customHeaders) {
    const incoming = new Headers(customHeaders);
    incoming.forEach((value, key) => headers.set(key, value));
  }
  if (body !== undefined) {
    if (typeof body === "object" && body !== null && !(body instanceof FormData)) {
      headers.set("Content-Type", "application/json");
      fetchInit.body = JSON.stringify(body);
    } else {
      fetchInit.body = body as BodyInit;
    }
  }
  const response = await apiFetch(url, { ...fetchInit, headers });
  const text = await response.text();
  const trimmed = text.trim();
  if (trimmed && bodyLooksLikeHtml(trimmed)) {
    throw new NonJsonResponseError(
      "The server returned a web page instead of data. Try a hard refresh or clear site data, then sign in again.",
      response.status,
      response.statusText
    );
  }
  const contentType = response.headers.get("content-type") ?? "";
  const treatAsJson =
    contentType.includes("application/json") ||
    contentType.includes("+json") ||
    /^\s*[[{]/.test(text);

  if (!treatAsJson) {
    if (!response.ok) {
      throw new Error(response.statusText || `HTTP ${response.status}`);
    }
    return undefined as unknown as T;
  }

  const data = parseJsonFromText(text, {
    status: response.status,
    statusText: response.statusText,
  }) as T;
  if (!response.ok) {
    if (!throwOnHttpError) {
      return data;
    }
    throw new Error(
      extractApiResponseErrorMessage(data, response.statusText || `HTTP ${response.status}`)
    );
  }
  return data;
}

export function getAuthHeaders(): Record<string, string> {
  return authHeader();
}

export function get<T = unknown>(
  url: string,
  params?: Record<string, string | number | boolean | undefined>
): Promise<T> {
  let fullUrl = url;
  if (params) {
    const entries = Object.entries(params).filter(
      (entry): entry is [string, string | number | boolean] =>
        entry[1] !== undefined && entry[1] !== null
    );
    const search = new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
    if (search) fullUrl = `${url}${url.includes("?") ? "&" : "?"}${search}`;
  }
  return authenticatedFetch<T>(fullUrl, { method: "GET" });
}

export function post<T = unknown>(url: string, body?: object): Promise<T> {
  return authenticatedFetch<T>(url, { method: "POST", body });
}

export function postAllowHttpError<T = unknown>(url: string, body?: object): Promise<T> {
  return authenticatedFetch<T>(url, { method: "POST", body, throwOnHttpError: false });
}

export function put<T = unknown>(url: string, body?: object): Promise<T> {
  return authenticatedFetch<T>(url, { method: "PUT", body });
}

export function patch<T = unknown>(url: string, body?: object): Promise<T> {
  return authenticatedFetch<T>(url, { method: "PATCH", body });
}

export function del<T = unknown>(url: string, body?: object): Promise<T> {
  return authenticatedFetch<T>(url, { method: "DELETE", body });
}
